/**
 * 管理员表格配置
 */
import React from "react";
import { Tag, Button } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";

// 管理员记录接口
export interface AdminRecord {
  id: string;
  admin_account: string;
  description: string; // 添加描述字段
  account_status: number;
  is_super_admin: boolean;
  last_login_at: string;
  created_at: string;
  updated_at: string;
  roles: string[];
  role_count: number;
  first_two_roles: string[];
  has_more_roles: boolean;
  permission_count?: number; // 权限数量
  permissions?: string[]; // 权限列表
}

// 表格列配置（接受回调函数）
export const getAdminTableColumns = (
  onToggleStatus?: (record: AdminRecord) => void,
  onAssignRole?: (record: AdminRecord) => void,
  onEdit?: (record: AdminRecord) => void,
  onDelete?: (record: AdminRecord) => void,
  onResetPassword?: (record: AdminRecord) => void,
  onViewPermissions?: (record: AdminRecord) => void
) => [
  {
    title: "用户名",
    dataIndex: "admin_account",
    key: "admin_account",
    width: 150,
  },
  {
    title: "描述",
    dataIndex: "description",
    key: "description",
    width: 200,
    render: (description: string) => {
      return <span style={{ color: description ? "#333" : "#999" }}>{description || "暂无描述"}</span>;
    },
  },
  {
    title: "管理员类型",
    dataIndex: "is_super_admin",
    key: "is_super_admin",
    width: 100,
    render: (isSuperAdmin: boolean) => {
      const text = isSuperAdmin ? "超级管理员" : "普通管理员";
      const color = isSuperAdmin ? "#1890ff" : "#666";
      return <span style={{ color }}>{text}</span>;
    },
  },

  {
    title: "角色",
    dataIndex: "roles",
    key: "roles",
    width: 200,
    render: (roles: any[], record: AdminRecord) => {
      const { first_two_roles, role_count, has_more_roles } = record;

      if (!first_two_roles || first_two_roles.length === 0) {
        return (
          <Tag
            color="default"
            style={{
              cursor: "pointer",
              border: "1px dashed #d9d9d9",
              background: "#fafafa",
            }}
            onClick={() => onAssignRole?.(record)}
            icon={<PlusOutlined />}
          >
            分配角色
          </Tag>
        );
      }

      const displayRoles = first_two_roles.map((role, index) => (
        <Tag
          key={index}
          color="blue"
          style={{ cursor: "pointer", marginBottom: "2px" }}
          onClick={() => onAssignRole?.(record)}
        >
          {role}
        </Tag>
      ));

      const moreText = has_more_roles ? ` +${role_count - first_two_roles.length}` : "";

      return (
        <div>
          {displayRoles}
          {moreText && (
            <Tag color="default" style={{ cursor: "pointer" }} onClick={() => onAssignRole?.(record)}>
              {moreText}
            </Tag>
          )}
        </div>
      );
    },
  },
  {
    title: "权限数量",
    dataIndex: "permission_count",
    key: "permission_count",
    width: 120,
    render: (count: number, record: AdminRecord) => {
      const permissionCount = count || record.permission_count || 0;
      console.log("权限数量列渲染:", { permissionCount, record, onViewPermissions: !!onViewPermissions });
      return (
        <Button
          type="link"
          size="small"
          onClick={() => {
            console.log("点击权限数量按钮:", record);
            onViewPermissions?.(record);
          }}
          style={{
            padding: "2px 8px",
            height: "auto",
            background: permissionCount > 0 ? "#e6f7ff" : "#f0f0f0",
            border: permissionCount > 0 ? "1px solid #91d5ff" : "1px solid #d9d9d9",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "bold",
            color: permissionCount > 0 ? "#1890ff" : "#666",
            cursor: "pointer",
            minWidth: "32px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title={permissionCount > 0 ? "点击查看权限详情" : "暂无权限"}
        >
          {permissionCount}
        </Button>
      );
    },
  },
  {
    title: "状态",
    dataIndex: "account_status",
    key: "account_status",
    width: 100,
    render: (status: number, record: AdminRecord) => {
      const isActive = status === 1;
      const color = isActive ? "success" : "error";
      const text = isActive ? "启用" : "禁用";

      return (
        <Tag
          color={color}
          style={{
            cursor: onToggleStatus ? "pointer" : "default",
            userSelect: "none",
          }}
          onClick={onToggleStatus ? () => onToggleStatus(record) : undefined}
          title={onToggleStatus ? `点击${isActive ? "禁用" : "启用"}管理员` : undefined}
        >
          {text}
        </Tag>
      );
    },
  },
  {
    title: "最后登录",
    dataIndex: "last_login_at",
    key: "last_login_at",
    width: 180,
    render: (date: string) => (date ? new Date(date).toLocaleString() : "从未登录"),
  },
  {
    title: "创建时间",
    dataIndex: "created_at",
    key: "created_at",
    width: 160,
    render: (date: string) => new Date(date).toLocaleString(),
  },
  {
    title: "操作",
    key: "action",
    width: 150,
    fixed: "right" as const,
    render: (record: AdminRecord) => (
      <div style={{ display: "flex", gap: "8px" }}>
        <Button
          type="text"
          icon={<EditOutlined />}
          size="small"
          onClick={() => onEdit?.(record)}
          title={record.is_super_admin ? "超级管理员不允许编辑" : "编辑管理员"}
          disabled={record.is_super_admin}
        />
        <Button
          type="text"
          icon={<ReloadOutlined />}
          size="small"
          onClick={() => onResetPassword?.(record)}
          title="重置密码"
        />
        <Button
          type="text"
          icon={<DeleteOutlined />}
          size="small"
          danger
          onClick={() => onDelete?.(record)}
          title={record.is_super_admin ? "超级管理员不允许删除" : "删除管理员"}
          disabled={record.is_super_admin}
        />
      </div>
    ),
  },
];

// 搜索配置
export const adminSearchConfig = {
  fields: [
    {
      name: "admin_account",
      label: "用户名",
      type: "input",
      placeholder: "请输入用户名",
    },
    {
      name: "description",
      label: "描述",
      type: "input",
      placeholder: "请输入描述关键词",
    },
    {
      name: "account_status",
      label: "状态",
      type: "select",
      options: [
        { label: "全部", value: "" },
        { label: "启用", value: "1" },
        { label: "禁用", value: "0" },
      ],
    },
    {
      name: "is_super_admin",
      label: "管理员类型",
      type: "select",
      options: [
        { label: "全部", value: "" },
        { label: "超级管理员", value: "true" },
        { label: "普通管理员", value: "false" },
      ],
    },
    {
      name: "role_name",
      label: "角色名称",
      type: "input",
      placeholder: "请输入角色名称",
    },
  ],
};

// 表格按钮配置接口（与 GenericPanelTable 的 TableButtonConfig 兼容）
export interface AdminTableButtonConfig {
  key: string;
  text?: string;
  icon?: React.ReactNode;
  type?: "default" | "primary" | "dashed" | "link" | "text" | "danger-border";
  ghost?: boolean;
  danger?: boolean;
  onClick: (selectedRows: any[], refreshData: () => void) => void;
  disabled?: boolean | ((selectedRows: any[]) => boolean);
  hidden?: boolean | ((selectedRows: any[]) => boolean);
  group?: "primary" | "secondary" | "tertiary";
  style?: React.CSSProperties;
}

// 表格按钮配置
export const getAdminTableButtons = (
  onRefresh: () => void,
  onCreate: () => void,
  onBatchDelete: () => void,
  selectedCount: number
): AdminTableButtonConfig[] => [
  {
    key: "batch_delete",
    text: `删除选中 (${selectedCount})`,
    icon: <DeleteOutlined />,
    type: "text",
    danger: true,
    group: "secondary",
    disabled: selectedCount === 0,
    style: {
      color: "#ff4d4f",
      border: "1px solid #ff4d4f",
      borderRadius: "4px",
    },
    onClick: (selectedRows: any[], refreshData: () => void) => {
      onBatchDelete();
    },
  },
  {
    key: "refresh_admins",
    text: "刷新",
    icon: <ReloadOutlined />,
    type: "default",
    group: "primary",
    onClick: (selectedRows: any[], refreshData: () => void) => {
      onRefresh();
    },
  },
  {
    key: "create_admin",
    text: "新建",
    icon: <PlusOutlined />,
    type: "primary",
    group: "primary",
    onClick: (selectedRows: any[], refreshData: () => void) => {
      onCreate();
    },
  },
];

// 表格组件配置
export const tableConfig = {
  title: "管理员列表",
  storageKey: "admin_management_table",
  scroll: {
    x: 1200,
    y: "100%", // 使用 100% 高度
  },
  showSelection: true,
  showColumnSetting: true,
  resizableColumns: true,
  saveColumnWidths: true,
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
  },
};
